/**
 * Qué queda en pie cuando el API le niega el log de eventos al portal.
 *
 * El detalle del partido tiene DOS orígenes (ver `MatchPage`): los documentos
 * ya digeridos (`/report`, la planilla, el reporte, el live por SSE) piden
 * `matches:read`, pero Rotaciones/Armador/Zonas/Evolución se derivan del log
 * crudo (`GET /events`), que pide `events:read`. El rol `viewer` —dirigente,
 * prensa— tiene el primero y no el segundo: recibe 403.
 *
 * Un 403 no se arregla reintentando ni cambiando el token: es el rol. Así que
 * el portal DEGRADA (muestra lo que sí puede) en vez de reventar, y este módulo
 * es esa política, pura y testeable. Los componentes sólo la aplican.
 */
import { ApiError } from '../../lib/api-client';
import type { MatchTab } from '../../lib/router';

/** El permiso que exige `GET /events` (id técnico del API, no se traduce). */
export const EVENTS_PERMISSION = 'events:read';

/**
 * Pestañas que se arman con el log crudo: sin `events:read` no hay NADA que
 * mostrar (no es "sin datos", es "sin permiso").
 *
 * `vivo` no está en la lista a propósito: el stream SSE entra con
 * `matches:read`, así que la pestaña sigue sirviendo el marcador y sólo pierde
 * el catch-up del log (lo resuelve `LiveTab`).
 */
export const TABS_NEEDING_EVENTS: readonly MatchTab[] = [
  'rotaciones',
  'armador',
  'zonas',
  'evolucion',
];

export function tabNeedsEvents(tab: MatchTab): boolean {
  return TABS_NEEDING_EVENTS.includes(tab);
}

/**
 * Cómo le fue al pedido del log, desde el punto de vista de la UI:
 *  - `ok`         → hay log (o todavía no falló): todo normal;
 *  - `forbidden`  → 403: el rol no llega, se degrada con un aviso neutro;
 *  - `error`      → red, 404, 500…: cartel rojo con «Reintentar», que sí sirve.
 */
export type EventAccess = 'ok' | 'forbidden' | 'error';

export function eventAccess(error: Error | null): EventAccess {
  if (error === null) return 'ok';
  if (error instanceof ApiError && error.kind === 'forbidden') return 'forbidden';
  return 'error';
}

/**
 * Permiso que nombra el aviso. El API lo manda en el cuerpo del 403
 * (`required`); si no vino, el de `/events` es el único candidato razonable.
 */
export function missingPermission(error: Error | null): string {
  if (error instanceof ApiError && error.requiredPermission !== null) {
    return error.requiredPermission;
  }
  return EVENTS_PERMISSION;
}
